import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { changeNodeAtPath, ExtendedNodeData, getNodeAtPath, RCCAlert, RCCButton, RCCCheckBox, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip, removeNode, toggleExpandedForAll } from "react-common-components";
import CheckPermission from "src/components/CheckPermission";
import LoadingContainer from "src/components/LoadingContainer";
import { IndicatorLevel } from "src/components/ModalIndicator/interface";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { IIndicatorsLevel } from "src/pages/Indicators/Panel/components/interface";
import { MapsHierarchyTypes } from "src/pages/StrategicPlanning/Maps/interface";
import { standardErrorMessages } from "src/utils/errors";
import { HierarchyMapItem, HierarchyMapRow, IHierarchyMaps, IRefHierarchyMaps } from "./interface";
import { StyledTarget, TextName } from "./styles";

const TreeHierarchyMaps: React.ForwardRefRenderFunction<IRefHierarchyMaps, IHierarchyMaps> = ({
	register,
	checkLevelHierarchy,
	lastLevelHierarchy,
	onCallbackButtonRow,
	selectOptions = "multi",
	unregister
}, ref) => {
	const { translate } = useTranslation(); 
	const { addToast } = RCCHookToast();

	const hierarchyDifRef = useRef<HTMLDivElement>(null);
	const [hierarchy, setHierarchy] = useState<any[]>([]);
	const [expandAll, setExpandAll] = useState(false);
	const [appliedExpandAll, setAppliedExpandAll] = useState(false);
	const [msgMovePermissionIndicator, setMsgMovePermissionIndicator] = useState(false);

	/** LOADING */
	const [loading, setLoading] = useState(false);
	const [errorLoading, setErrorLoading] = useState(false);

	// Feito dessa maneira para ser similar para o node ter as propriedades necessárias pra funcionar corretamente

	const { checkIfHasSpecificPermission } = useAuth();


	const HierarchyNode: React.FC<ExtendedNodeData> = ({
		node
	}) => (<RCCGrid 
		container
		item
		alignItems="center"
		styleInline={`color:${node.color}`}
		onClick={(e) => e.stopPropagation()}
	>
		{
			(Array.isArray(checkLevelHierarchy) && checkLevelHierarchy.includes(node.level))  ||
				checkLevelHierarchy === node.level ? (
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCCheckBox 
							styleInline={{checkbox: "margin-right: 5px;"}}
							name={`selectedItens-${node.level}.item-${node.id}`} 
							register={register} 
							value={node.id}
							defaultChecked={node.checked }
							onClick={(e: React.MouseEvent<HTMLInputElement>) => {
								e.stopPropagation();
								if(selectOptions === "single")
									selectOnlySingleOption(e.target as HTMLInputElement);
							}}
						/>
					</CheckPermission>
				) : null
		}
		<RCCGrid 
			container
			item
			alignItems="center"
			xs={11}
		>
			{
				node.level === MapsHierarchyTypes.INDICATORS && node.nivel === IIndicatorsLevel.STRATEGIC ? 
					<StyledTarget /> :
					<>
						{
							node.level !== MapsHierarchyTypes.INICIATIVES &&
							<RCCIcon name={setTypeIcon(node.level, node.nivel)} style={{ marginRight: "5px", marginLeft: "5px", color: "var(--input-font-color)" }} />
						}
					</>
			}

			<TextName>
				<span>{setTypeNameLevel(node.level)}:</span> {node.name}
			</TextName>
		</RCCGrid>
	</RCCGrid>);

	const showItens = async () => {
		try{
			
			let dataForm: any[] = hierarchy;
			if(!expandAll && !appliedExpandAll){
				setLoading(true);


				const {data} = await RCCServicesApi.getRequest({
					method: "Maps/ListHierarchyAll",
					urlParameters: {
						lastLevel: 	lastLevelHierarchy
					}
				});

				dataForm = data;
				setAppliedExpandAll(true);
				setLoading(false);
			}

			const newitens = toggleExpandedForAll({treeData:[...dataForm],expanded:!expandAll});
			setHierarchy(newitens);
			setExpandAll(!expandAll);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os itens"), addToast);
			setLoading(false);
			setErrorLoading(true);
		}
	};

	const getNodeKeyFunction =({node}:any) => `${node.id}-${node.level}}`;

	const removeItem = (path:Array<string|number>) => {
		const data = removeNode({treeData:[...hierarchy],path,getNodeKey:getNodeKeyFunction});
		setHierarchy(data?.treeData||[]);
	};

	const addItem = async (row: any) => {
		const {data} = await RCCServicesApi.getRequest({
			method: "Maps/ListHierarchy",
			urlParameters: {
				parent: row ? row.node.id : "",
				level: MapsHierarchyTypes.INDICATORS,
				lastLevel: lastLevelHierarchy
			}
		});

		modifyHierarchy(row, data);
	};

	const getNodePath = (path:Array<string|number>) => getNodeAtPath({ 
		treeData:[...hierarchy],
		getNodeKey: getNodeKeyFunction,
		path
	})?.node;

	const getHierarchy = async (row: any) => {
		try{
			if(row && (row.node.hasChildren === false || row.node.expanded || row.node.children.filter((x: any) => x.id !== 0).length)){
				return;
			}
			setLoading(true);

			let level = MapsHierarchyTypes.MAPS;

			if(row){
				if(row.path.length === MapsHierarchyTypes.OBJECTIVES){
					level = lastLevelHierarchy === MapsHierarchyTypes.INDICATORS ? 5 : 4;
				}else{
					level = row.path.length+1;
				}
			}

			const {data} = await RCCServicesApi.getRequest({
				method: "Maps/ListHierarchy",
				urlParameters: {
					parent: row ? row.node.id : "",
					level,
					lastLevel: lastLevelHierarchy
				}
			});

			if(row == null){
				setHierarchy(data.map((elem: any) => ({
					...elem,
					children: elem.hasChildren ? [{id: 0, name: ""}] : []
				})));
			}else{

				modifyHierarchy(row, data);
			}
			
			setLoading(false);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os itens"), addToast);
			setLoading(false);
			setErrorLoading(true);
		}
	};

	const modifyHierarchy = (row: any, data: any) => {
		const modifiedNode = getNodeAtPath({ 
			treeData:[...hierarchy],
			getNodeKey: getNodeKeyFunction,
			path: row.path
		})?.node;

		if(modifiedNode){
			modifiedNode.children = data.map((elem: any) => ({
				...elem,
				children: elem.hasChildren ? [{id: 0, name: ""}] : []
			}));
			modifiedNode.expanded = true;

			const newTree = changeNodeAtPath({
				treeData:[...hierarchy],
				path:row.path,
				getNodeKey:getNodeKeyFunction,
				newNode:modifiedNode
			});

			setHierarchy(newTree);
		}
	};

	const setTypeIcon = (level: number, nivel?: string) => {
		switch (level) {
		case 1:
			return "map";
		case 2: 
			return "mapSigns";
		case 3:
			return "mapMarker";
		case 5: 
			switch(nivel){
			case IIndicatorsLevel.OPERATIONAL:
				return "tools";
			case IndicatorLevel.TATIC:
				return "chessKnight";
			default:
				return "pencil";
			}
		default:
			return "pencil";
		}
	};

	const setTypeNameLevel = (level: number) => {
		switch (level) {
		case 1:
			return translate("Mapa");
		case 2: 
			return translate("Perspectiva");
		case 3:
			return translate("Objetivo");
		case 4:
			return translate("Iniciativa");
		case 5: 
			return translate("Indicador");
		default:
			return "";
		}
	};

	useEffect(() => {getHierarchy(null);}, []);

	const selectOnlySingleOption = (target: HTMLInputElement) => {
		const inputs = hierarchyDifRef.current?.querySelectorAll(`input:not([id*="${target.id}"])`);

		if(!inputs)
			return;

		inputs.forEach((input) => {
			const inputElement = input as HTMLInputElement;
			unregister(inputElement.name);
			inputElement.checked = false;
		});
	};

	const handleTreeMove = async(data: any) => {
		try{
			const {prevPath} = data;
			prevPath.pop();
			const nodeObjective = getNodePath(prevPath);

			const metba004 = nodeObjective.children.map((item:HierarchyMapItem, index: number) => ({
				mea004Id: item.id,
				mea004Iqc001Id: item.id2,
				mea004Mea003Id: nodeObjective.id,
				mea004Ordem: index + 1
			}));

			await RCCServicesApi.postRequest({
				method: "Objectives/UpdateOrderBondIndicators",
				body:metba004
			});
		}catch(error){
			standardErrorMessages(error, translate("Erro ao atualizar hierarquia"), addToast);
		}
	};

	const canDrag = (row: any) => {
		if(!checkIfHasSpecificPermission("maps/hierarchy", PermissionEnum.CREATE_UPDATE))
			return false;
		if(row.node.level === MapsHierarchyTypes.INDICATORS)
			return true;
		return false;
	};

	const canDrop = (row: HierarchyMapRow) => {	
		if(row.prevParent.id === row.nextParent.id){
			setMsgMovePermissionIndicator(false);
			return true;
		}
		
		if(row.prevPath.length === row.nextPath.length){
			setTimeout(() => {
				setMsgMovePermissionIndicator(true);
			}, 2000);
		}
		return false;
	};

	useEffect(() => {
		if(msgMovePermissionIndicator === true){
			addToast({
				color: "yellow",
				description: translate("Indicador não pode ser movido para outro objetivo"),
				title: translate("Aviso")
			});
		}
	}, [msgMovePermissionIndicator]);

	useImperativeHandle(ref,() => ({
		removeItem,
		addItem,
		getNodePath
	}));

	return (
		<RCCGrid styleInline="width: 100%">
			<LoadingContainer loading={loading} hideContent={hierarchy && hierarchy.length === 0}>
				{errorLoading ? (
					<RCCAlert 
						title={translate("Atenção")}
						description={translate("Erro ao buscar as iniciativas")}
					/>
				) : (
					<>
						{
							hierarchy && hierarchy.length === 0 
								? (<RCCAlert 
									title={translate("Atenção")}
									description={translate("Nenhum resultado encontrado")}
								/>)
								: 
								(
									<>
										<RCCGrid container>
											<RCCGrid 
												container 
												justifyContent="flex-end"
											>
												<RCCTooltip
													text={expandAll ? translate("Recolher todos") : translate("Expandir todos")}
													position="left"
												>
													<RCCButton 
														onClick = {() => {showItens();}}
														icon = {{
															name: expandAll ? "compress" : "expand"
														}}
														layout="isRounded"
														size="md"
													/>
												</RCCTooltip>
											</RCCGrid>
											<div 
												ref={hierarchyDifRef}
												style={{ width: "100%" }}
											>
												<RCCHierarchicalTree
													data={hierarchy}
													onMoveNode={handleTreeMove}
													getNodeKey={getNodeKeyFunction}
													onChange={(treeData) => setHierarchy(treeData)}
													generateNodeProps={(row: ExtendedNodeData) => ({
														title: 
														<HierarchyNode 
															node= {row.node}
															path={row.path} 
															treeIndex={row.treeIndex} 
															lowerSiblingCounts={row.lowerSiblingCounts} 
															isSearchMatch={row.isSearchMatch}
															isSearchFocus={row.isSearchFocus}
															parentNode={row.parentNode}
														/>
														,
														onClick: () => getHierarchy(row),
														buttons: onCallbackButtonRow ? onCallbackButtonRow(row) : []
													})}
													canDrop={canDrop}
													canDrag={canDrag}
													rowHeight={50}
												/>
											</div>
										</RCCGrid>
									</>
								)
						}
					</>
				)}
			</LoadingContainer>
		</RCCGrid>
	);
};
	
export default forwardRef(TreeHierarchyMaps);
